/**
 * 說明文件的內容——跟 `native/lib/help_content.dart` 兩邊各存一份。
 *
 * 為什麼複製而不共享：一邊是 TypeScript 一邊是 Dart，都是純文案沒有邏輯，
 * 中間套 build step 讓兩邊讀同一個 JSON 弊多於利（多一個 build 依賴、
 * IDE 跳轉沒法直接看內容）。改內容時要一起改，這個成本比互通機制小。
 *
 * 若日後語言、平台再多，或內容改成大量 CMS 化，再重看這個決策。
 */

import type { Locale } from './i18n.ts'

export type HelpBlock =
  | { kind: 'para'; text: string }
  | { kind: 'bullets'; items: string[] }

export interface HelpSection {
  title: string
  blocks: HelpBlock[]
}

export function helpContent(locale: Locale): HelpSection[] {
  return locale === 'en' ? EN : ZH_HANT
}

const ZH_HANT: HelpSection[] = [
  {
    title: 'QDesktop 是什麼',
    blocks: [
      {
        kind: 'para',
        text:
          'QDesktop 把「工具、資料、代理人」放在一片可以自己排的桌面上。每個工具是一片「元件」，你決定要哪些、擺哪裡。' +
          '登入的 QSyn 帳號負責同步——不管從手機或網頁打開，看到的是同一片桌面。',
      },
    ],
  },
  {
    title: '桌面',
    blocks: [
      {
        kind: 'bullets',
        items: [
          '一個帳號可以有多張桌面，從**桌面選擇**畫面挑一張進去。',
          '每張桌面的元件配置各自獨立，配對的代理人也獨立。',
          '進到桌面之後，按下面的桌面名稱膠囊可以回到選擇畫面。',
          '新增：頁面右上 ＋；重新命名 / 刪除：卡片右邊的三個點。',
        ],
      },
    ],
  },
  {
    title: '元件',
    blocks: [
      {
        kind: 'bullets',
        items: [
          '從桌面底部的 ＋ 開新元件。',
          '拖標題列搬動、拖右下角改大小。',
          '標題列的按鈕：說明、設定、最小化、最大化、關閉。',
          '全螢幕的元件會延伸到狀態列，標題列仍然可以拖。',
        ],
      },
    ],
  },
  {
    title: '鎖住視窗',
    blocks: [
      {
        kind: 'para',
        text: '底部 🔒 打開鎖定清單。鎖住的元件標題列會收起、位置固定，重新整理也不會跑。',
      },
      {
        kind: 'para',
        text:
          '適合「不要碰它、就是要它在那裡」的元件——例如常駐儀表板或通知條。' +
          '想改的時候在鎖定清單那顆解鎖。',
      },
      {
        kind: 'para',
        text: '如果一個元件被鎖住又是全螢幕，「還原並置中」會把它拉回中央、正常大小。',
      },
    ],
  },
  {
    title: 'Dock（底部那條）',
    blocks: [
      {
        kind: 'bullets',
        items: [
          '桌面名稱、新增、鎖定、說明按鈕都住在 dock 上。',
          '最左邊 ⋮⋮ 是拖曳把手，按住可以搬 dock 到任何位置。',
          '第二顆 ⇵ 切換橫式 / 直式。',
          '位置與方向**每張桌面分開記憶**，換張桌面 dock 會回到那張桌面上次的樣子。',
        ],
      },
    ],
  },
  {
    title: '帳號設定',
    blocks: [
      {
        kind: 'para',
        text: '網頁版目前經由 QSyn 主站管理帳號與訂閱。原生 app 版本則從**桌面選擇**畫面右上頭像進入。',
      },
      {
        kind: 'bullets',
        items: [
          '**使用狀態**：credits、代理人與桌面用量。',
          '**訂閱**：切換方案，付款走 QSyn 網站。',
          '**個人資料**：改名、Email、登出、刪除帳號（在 QSyn 網站）。',
          '**語言**：介面語言（繁中 / 英文）。桌面裡的元件也跟著換。',
          '**說明文件**：這一份文件。',
        ],
      },
    ],
  },
  {
    title: '語音代理人',
    blocks: [
      {
        kind: 'bullets',
        items: [
          '桌面上開語音元件，按錄音鈕開始講話。',
          '代理人聽到後即時回應，也會在 transcript 記錄下來。',
          '在原生 app 上，鎖螢幕或切背景 30 秒內可以繼續講；久了 iOS 會中斷通話。',
        ],
      },
    ],
  },
  {
    title: '資料與同步',
    blocks: [
      {
        kind: 'bullets',
        items: [
          '桌面配置存在 QSyn，離線也能改（本機一份）。',
          '有網路時自動同步，dock 上會出現「已儲存 / 儲存中 / 沒有同步」燈號。',
          '「已連線」膠囊顯示 QSyn 的裝置通道狀態——出事的時候看它。',
        ],
      },
    ],
  },
  {
    title: '遇到問題',
    blocks: [
      {
        kind: 'bullets',
        items: [
          '**連不上 QSyn**：檢查網路。狀態不對就重新登入。',
          '**變更沒同步**：儲存燈號寫「沒有同步」時，資料在本機，恢復連線後會自動送。',
          '**未配對**：桌面名稱旁邊寫「未配對」代表這台裝置沒被 QSyn 記錄，代理人碰不到；再挑一次桌面就會重新配對。',
          '**元件出錯**：標題列的「重新載入」按鈕。',
        ],
      },
    ],
  },
]

const EN: HelpSection[] = [
  {
    title: 'What QDesktop is',
    blocks: [
      {
        kind: 'para',
        text:
          'QDesktop puts tools, data and agents onto a desktop you arrange yourself. ' +
          'Each tool is a widget, and you decide which ones and where they go. ' +
          'Your QSyn account keeps everything in sync — phone or web, same desktop.',
      },
    ],
  },
  {
    title: 'Desktops',
    blocks: [
      {
        kind: 'bullets',
        items: [
          'One account can hold several desktops. Pick one from the **Choose a desktop** screen.',
          'Each desktop has its own widget layout and its own paired agents.',
          'Inside a desktop, tap the desktop-name pill to go back to the picker.',
          'Create: **+** at the top right. Rename or delete: the three-dot menu on each card.',
        ],
      },
    ],
  },
  {
    title: 'Widgets',
    blocks: [
      {
        kind: 'bullets',
        items: [
          'Add a widget from the **+** on the bottom dock.',
          'Drag the title bar to move; drag the bottom-right corner to resize.',
          'Title-bar buttons: help, settings, minimise, maximise, close.',
          'A full-screen widget extends into the status bar; its title bar is still draggable.',
        ],
      },
    ],
  },
  {
    title: 'Locking a window',
    blocks: [
      {
        kind: 'para',
        text:
          'Open the lock list from the 🔒 on the dock. A locked widget hides its title bar ' +
          'and stays put — reloads and reopens do not move it.',
      },
      {
        kind: 'para',
        text:
          "Good for widgets you don't want to touch — a permanent dashboard or a notifier. " +
          'Unlock from the same list when you want to change it.',
      },
      {
        kind: 'para',
        text:
          'If a widget is both locked and full-screen, "Restore and centre" brings it back ' +
          'to a normal size in the middle of the screen.',
      },
    ],
  },
  {
    title: 'Dock',
    blocks: [
      {
        kind: 'bullets',
        items: [
          'The desktop-name pill, add, lock and help buttons all live on the dock.',
          'The leftmost ⋮⋮ is a drag handle — hold and drag to move the whole dock.',
          'The second button ⇵ toggles horizontal / vertical layout.',
          'Position and orientation are **remembered per desktop**, so switching desktops brings back the dock the way you left it there.',
        ],
      },
    ],
  },
  {
    title: 'Account',
    blocks: [
      {
        kind: 'para',
        text:
          'On the web, account and subscription are managed on the QSyn main site. ' +
          'On the native app, open from the avatar at the top right of the **Choose a desktop** screen:',
      },
      {
        kind: 'bullets',
        items: [
          '**Usage** — credits, agent and desktop consumption.',
          '**Subscription** — change plan; payment happens on the QSyn website.',
          '**Profile** — rename, email, sign out, delete account (on the QSyn website).',
          '**Language** — interface language (Traditional Chinese / English). Desktop widgets follow the same choice.',
          '**Help** — this document.',
        ],
      },
    ],
  },
  {
    title: 'Voice agent',
    blocks: [
      {
        kind: 'bullets',
        items: [
          'Open the voice widget on a desktop, tap the mic to start talking.',
          'The agent replies in real time and shows a transcript.',
          'On the native app, locking the screen or backgrounding is fine for about 30 seconds; longer and iOS will drop the call.',
        ],
      },
    ],
  },
  {
    title: 'Data and sync',
    blocks: [
      {
        kind: 'bullets',
        items: [
          'Desktop layout is stored in QSyn, and edits work offline (a local copy stays).',
          'When online, it syncs automatically — the dock shows Saved / Saving / Not synced.',
          'The "Connected" pill shows the QSyn device channel — check it when something goes wrong.',
        ],
      },
    ],
  },
  {
    title: 'Troubleshooting',
    blocks: [
      {
        kind: 'bullets',
        items: [
          "**Can't reach QSyn** — check the network. If it stays stuck, sign in again.",
          '**Changes not synced** — when the dock says Not synced, the data is safe on this device and will be sent when the connection is back.',
          "**Not paired** — a Not paired label next to the desktop name means this device is not recorded on QSyn (so agents can't reach it). Pick the desktop again to re-pair.",
          '**Widget broken** — use the Reload button on its title bar.',
        ],
      },
    ],
  },
]
